import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import initialInspectionReportsService from "../services/initialInspectionReportsService";
import s3Service from "../services/s3Service";
import PhotoGallery from "../components/PhotoGallery";
import styles from "./InitialInspectionCreate.module.css";
import {
  saveDraft,
  deleteDraft,
  getResumableDraftByProperty,
  OFFLINE_PHOTO_PREFIX,
} from "../lib/offlineDb";
import { triggerSync } from "../lib/inspectionSyncEngine";

const INITIAL_INSPECTION_IMAGE_FIELDS = [
  { field: "image_gas_meters", label: "Gas Meters" },
  { field: "image_electric_meters", label: "Electric Meters" },
  { field: "image_water_meters", label: "Water Meters" },
  { field: "image_fuse_boards", label: "Fuse Boards" },
];

const InitialInspectionCreate = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [property, setProperty] = useState(null);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [roomSizes, setRoomSizes] = useState({});
  const [roomsNumber, setRoomsNumber] = useState(0);
  const [errors, setErrors] = useState({});
  const [fileNames, setFileNames] = useState({
    image_gas_meters: "No file chosen",
    image_electric_meters: "No file chosen",
    image_water_meters: "No file chosen",
    image_fuse_boards: "No file chosen",
  });

  // Large image viewer + rotation persistence
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [photoRotations, setPhotoRotations] = useState({});
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState({});

  const rotatePhotoField = (field) => {
    setPhotoRotations((prev) => ({
      ...prev,
      [field]: ((prev[field] || 0) + 90) % 360,
    }));
  };

  // Refs for date picker inputs
  const reportDateInputRef = useRef(null);
  const issuedDateInputRef = useRef(null);
  const reviewDateInputRef = useRef(null);

  const [formData, setFormData] = useState({
    property_id: propertyId,
    status_id: 0,
    address: "",
    client: "",
    what_council: "",
    building_type: "",
    property_for_voa: 0,
    floors: "",
    toilets: "",
    bathrooms: "",
    kitchens: "",
    parking: 0,
    garden: 0,
    rooms_number: 0,
    rooms: [],
    maintenance: "",
    installation_shower_cubicles: "",
    installation_shower_electric_units: "",
    installation_kitchens: "",
    installation_electric_heaters: "",
    installation_over_sink_water_heaters: "",
    installation_fridges: "",
    installation_mini_ovens: "",
    installation_microwaves: "",
    installation_cookers: "",
    locations_of_meters: "",
    image_gas_meters: null,
    image_electric_meters: null,
    image_water_meters: null,
    image_fuse_boards: null,
    utility_bills_client: "",
    utility_bills_ggm: "",
    waste_collection_client: "",
    waste_collection_ggm: "",
    surveyed_by: "",
    signed: "",
    report_date: new Date().toLocaleDateString("en-GB"),
    form: "Q15",
    issued: new Date().toLocaleDateString("en-GB"),
    review_date: new Date().toLocaleDateString("en-GB"),
    approved: "",
    notes: "",
  });

  // ---------------------------------------------------------------------------
  // Offline upload support (initial inspection — create mode)
  // ---------------------------------------------------------------------------
  const genUuid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

  const draftRef = useRef({ id: null, clientUuid: null, photos: {} });
  const draftActiveRef = useRef(false);
  const objectUrlsRef = useRef({});
  const formDataRef = useRef(formData);
  const photoRotationsRef = useRef(photoRotations);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    photoRotationsRef.current = photoRotations;
  }, [photoRotations]);

  const ensureDraftId = () => {
    if (!draftRef.current.id) {
      draftRef.current.id = genUuid();
      draftRef.current.clientUuid = genUuid();
      if (!draftRef.current.photos) draftRef.current.photos = {};
    }
  };

  // Snapshot the form, forcing each buffered image field to its filename (if
  // already uploaded) or an offline placeholder.
  const snapshotFormData = () => {
    const fd = { ...formDataRef.current };
    const files = draftRef.current.photos || {};
    Object.keys(files).forEach((key) => {
      const rec = files[key];
      if (rec && rec.field) {
        fd[rec.field] = rec.s3Key
          ? rec.s3Key.split("/").pop()
          : OFFLINE_PHOTO_PREFIX + key;
      }
    });
    return fd;
  };

  const persistDraft = async (status) => {
    ensureDraftId();
    await saveDraft({
      id: draftRef.current.id,
      propertyId: String(propertyId),
      reportType: "initial",
      mode: "create",
      clientUuid: draftRef.current.clientUuid,
      status,
      formData: snapshotFormData(),
      photoRotations: photoRotationsRef.current || {},
      photos: draftRef.current.photos || {},
      attempts: 0,
      permanent: false,
      lastError: null,
    });
    draftActiveRef.current = true;
  };

  // Buffer an image selected while offline. `field` keys it; stored value is the
  // filename (initial inspections store filename only). Throws on storage failure.
  const handleOfflineSave = async (file, field) => {
    ensureDraftId();
    const prev = draftRef.current.photos[field];
    draftRef.current.photos[field] = {
      blob: file,
      fileName: file.name,
      mimeType: file.type,
      folder: `uploads/images/properties/${propertyId}/reports/iir/`,
      field,
      filenameOnly: true,
    };
    try {
      await persistDraft("draft");
    } catch (err) {
      if (prev) draftRef.current.photos[field] = prev;
      else delete draftRef.current.photos[field];
      throw err;
    }
    return OFFLINE_PHOTO_PREFIX + field;
  };

  const queueForSync = async () => {
    await persistDraft("queued");
    toast.info(
      "Saved. This report will upload automatically when you’re back online."
    );
    triggerSync();
    navigate(`/properties/${propertyId}`);
  };

  const restoreDraft = async () => {
    try {
      const saved = await getResumableDraftByProperty(propertyId, "initial");
      if (!saved) return;
      draftRef.current = {
        id: saved.id,
        clientUuid: saved.clientUuid,
        photos: saved.photos || {},
      };
      draftActiveRef.current = true;
      if (saved.formData) setFormData((prev) => ({ ...prev, ...saved.formData }));
      if (saved.photoRotations) setPhotoRotations(saved.photoRotations);
      if (saved.formData && saved.formData.rooms_number != null)
        setRoomsNumber(saved.formData.rooms_number);
      // Rebuild previews + filename labels from buffered Blobs.
      const previews = {};
      const names = {};
      Object.values(saved.photos || {}).forEach((rec) => {
        if (rec && rec.blob && rec.field) {
          const url = URL.createObjectURL(rec.blob);
          objectUrlsRef.current[rec.field] = url;
          previews[rec.field] = url;
          names[rec.field] = `${rec.fileName || "photo"} (saved offline)`;
        }
      });
      if (Object.keys(previews).length)
        setPhotoPreviewUrls((p) => ({ ...p, ...previews }));
      if (Object.keys(names).length)
        setFileNames((p) => ({ ...p, ...names }));
      toast.info("Resumed your saved offline inspection report.");
    } catch (e) {
      console.warn("[IIR create] restore draft failed:", e && e.message);
    }
  };

  useEffect(() => {
    if (!draftActiveRef.current) return undefined;
    const t = setTimeout(() => {
      persistDraft("draft").catch(() => {});
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, photoRotations]);

  useEffect(
    () => () => {
      Object.values(objectUrlsRef.current).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {
          /* no-op */
        }
      });
    },
    []
  );

  useEffect(() => {
    loadInitialData();
  }, [propertyId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load form data from API
      const response = await initialInspectionReportsService.getFormData(
        propertyId
      );

      if (response.success) {
        const data = response.data;
        setProperty(data.property);
        setBuildingTypes(data.buildingTypes);
        setRoomSizes(data.roomSizes);
        setRoomsNumber(data.roomsNumber);

        // Set form data with property information
        setFormData((prev) => ({
          ...prev,
          property_id: propertyId, // Ensure property_id is preserved
          address: data.property.fullAddress || "",
          client: typeof data.property.client === 'object' && data.property.client !== null 
            ? data.property.client.name || "" 
            : data.property.client || "",
          bathrooms: data.property.bathrooms || "",
          kitchens: data.property.kitchens || "",
          parking: data.property.parking || 0,
          garden: data.property.garden || 0,
          rooms_number: data.roomsNumber,
          rooms: Array(data.roomsNumber).fill(""),
          report_date: data.now,
          issued: data.now,
          review_date: data.now,
        }));

        // Resume any saved offline draft for this property (after prefill).
        await restoreDraft();
      } else {
        toast.error("Failed to load form data");
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast.error("Failed to load property data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        // Validate file type (jpg, jpeg, png)
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          toast.error('Please select a valid image file (JPG, JPEG, or PNG)');
          return;
        }

        // Validate file size (max 100MB as per Laravel)
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          toast.error('File size must be less than 100MB');
          return;
        }

        // Upload to S3
        handleFileUpload(file, name);
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: null,
        }));
        setFileNames((prev) => ({
          ...prev,
          [name]: "No file chosen",
        }));
      }
    } else if (type === "number") {
      const numValue = value === "" ? "" : parseInt(value);
      // Prevent negative values for number inputs
      if (numValue !== "" && numValue < 0) {
        return; // Don't update state with negative values
      }
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleFileUpload = async (file, fieldName) => {
    // Laravel path: uploads/images/properties/{property_id}/reports/iir/
    const folder = `uploads/images/properties/${propertyId}/reports/iir/`;

    // Capture a local data URL so the thumbnail/lightbox can show the same image
    // the user just picked, before the report is saved (works offline too).
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreviewUrls((prev) => ({ ...prev, [fieldName]: e.target.result }));
    };
    reader.readAsDataURL(file);

    const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering

    // Buffer the image locally for later sync (offline / online failure).
    const bufferOffline = async () => {
      if (file.size > OFFLINE_MAX_SIZE) {
        toast.error(
          "While offline, images must be under 25MB. Upload larger files when you’re back online."
        );
        setFileNames((prev) => ({ ...prev, [fieldName]: "No file chosen" }));
        setPhotoPreviewUrls((prev) => {
          const n = { ...prev };
          delete n[fieldName];
          return n;
        });
        return;
      }
      setFileNames((prev) => ({ ...prev, [fieldName]: "Saving offline..." }));
      try {
        const placeholder = await handleOfflineSave(file, fieldName);
        setFormData((prev) => ({ ...prev, [fieldName]: placeholder }));
        setFileNames((prev) => ({
          ...prev,
          [fieldName]: `${file.name} (saved offline)`,
        }));
      } catch (err) {
        setFileNames((prev) => ({ ...prev, [fieldName]: "No file chosen" }));
        setPhotoPreviewUrls((prev) => {
          const n = { ...prev };
          delete n[fieldName];
          return n;
        });
        const full =
          /quota/i.test(err?.message || "") || err?.name === "QuotaExceededError";
        toast.error(
          full
            ? "This device is out of storage, so the image could not be saved for offline upload."
            : "Offline storage is unavailable, so the image cannot be saved while offline. Try again once you are back online."
        );
      }
    };

    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
    if (!isOnline) {
      await bufferOffline();
      return;
    }

    try {
      setFileNames((prev) => ({ ...prev, [fieldName]: "Uploading..." }));

      // Bound the online upload so a dead zone falls back to offline buffering.
      const s3Key = await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("Upload timed out")), 30000);
        s3Service
          .uploadImage(file, {
            maxSize: 100 * 1024 * 1024, // 100MB
            allowedTypes: ["image/jpeg", "image/jpg", "image/png"],
            folder: folder,
            useLaravelStructure: true,
            onProgress: (progress) => {
              setFileNames((prev) => ({
                ...prev,
                [fieldName]: `Uploading... ${Math.round(progress)}%`,
              }));
            },
          })
          .then((k) => {
            clearTimeout(timer);
            resolve(k);
          })
          .catch((e) => {
            clearTimeout(timer);
            reject(e);
          });
      });

      // Database stores just the filename, not the full S3 path.
      const filename = s3Key.split("/").pop();
      setFormData((prev) => ({ ...prev, [fieldName]: filename }));
      setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
      // Drop any earlier offline buffer for this field now it's uploaded.
      if (draftRef.current.photos[fieldName]) {
        delete draftRef.current.photos[fieldName];
      }
      toast.success(
        `${fieldName.replace("image_", "").replace("_", " ")} uploaded successfully`
      );
    } catch (error) {
      console.error("File upload error:", error);
      // Network/timeout failure → buffer offline instead of losing the image.
      await bufferOffline();
    }
  };

  const handleRoomsNumberChange = (e) => {
    const newRoomsNumber = parseInt(e.target.value) || 0;
    // Prevent negative values
    if (newRoomsNumber < 0) {
      return;
    }
    setRoomsNumber(newRoomsNumber);

    setFormData((prev) => ({
      ...prev,
      rooms_number: newRoomsNumber,
      rooms: Array(newRoomsNumber).fill(""),
    }));

    // Clear room-related errors when rooms number changes
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.rooms_number;
      delete newErrors.rooms;
      // Clear individual room errors
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith('rooms.')) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const handleRoomChange = (index, value) => {
    const newRooms = [...formData.rooms];
    newRooms[index] = value;

    setFormData((prev) => ({
      ...prev,
      rooms: newRooms,
    }));

    // Clear error for this specific room
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`rooms.${index}`];
      delete newErrors.rooms; // Clear general rooms error
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Check required fields based on Laravel validation
    if (!formData.status_id && formData.status_id !== 0) {
      newErrors.status_id = "Status is required";
    }

    if (!formData.rooms_number && formData.rooms_number !== 0) {
      newErrors.rooms_number = "Number of rooms is required";
    }

    // Validate rooms if rooms_number is set
    if (formData.rooms_number > 0) {
      formData.rooms.forEach((room, index) => {
        if (!room || room === "") {
          newErrors[`rooms.${index}`] = `Room ${index + 1} is required`;
        }
      });
    }

    // Integer field validations (min:0) - matching Laravel validation
    const integerFields = [
      'floors', 'toilets', 'bathrooms', 'kitchens',
      'installation_shower_cubicles', 'installation_shower_electric_units',
      'installation_kitchens', 'installation_electric_heaters',
      'installation_over_sink_water_heaters', 'installation_fridges',
      'installation_mini_ovens', 'installation_microwaves', 'installation_cookers'
    ];

    integerFields.forEach(field => {
      const value = formData[field];
      if (value !== undefined && value !== null && value !== "") {
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 0) {
          newErrors[field] = `${field.replace(/_/g, ' ')} must be a non-negative number`;
        }
      }
    });

    // Date format validation (DD/MM/YYYY) - matching Laravel validation
    const dateFields = ['report_date', 'issued', 'review_date'];
    const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

    dateFields.forEach(field => {
      const value = formData[field];
      if (value && value.trim() !== "") {
        if (!datePattern.test(value)) {
          newErrors[field] = `${field.replace(/_/g, ' ')} must be in DD/MM/YYYY format`;
        } else {
          // Validate actual date values
          const [, day, month, year] = value.match(datePattern);
          const dayNum = parseInt(day);
          const monthNum = parseInt(month);
          const yearNum = parseInt(year);

          if (monthNum < 1 || monthNum > 12) {
            newErrors[field] = `${field.replace(/_/g, ' ')} has invalid month`;
          } else if (dayNum < 1 || dayNum > 31) {
            newErrors[field] = `${field.replace(/_/g, ' ')} has invalid day`;
          } else if (yearNum < 1900 || yearNum > 2100) {
            newErrors[field] = `${field.replace(/_/g, ' ')} has invalid year`;
          }
        }
      }
    });

    // String max length validations (255 chars) - matching Laravel validation
    const stringFields = [
      'client', 'what_council', 'building_type',
      'utility_bills_client', 'utility_bills_ggm',
      'waste_collection_client', 'waste_collection_ggm',
      'surveyed_by', 'signed', 'form', 'approved'
    ];

    stringFields.forEach(field => {
      const value = formData[field];
      if (value && value.length > 255) {
        newErrors[field] = `${field.replace(/_/g, ' ')} must not exceed 255 characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle DD/MM/YYYY format (Laravel format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return '';
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY (Laravel format)
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Handle date change from native date picker
  const handleDateChange = (fieldName, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      setFormData(prev => ({
        ...prev,
        [fieldName]: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Open date picker
  const openDatePicker = (fieldName, e) => {
    e?.preventDefault();
    let dateInputRef;
    if (fieldName === 'report_date') {
      dateInputRef = reportDateInputRef;
    } else if (fieldName === 'issued') {
      dateInputRef = issuedDateInputRef;
    } else if (fieldName === 'review_date') {
      dateInputRef = reviewDateInputRef;
    }
    
    if (dateInputRef?.current) {
      setTimeout(() => {
        try {
          if (dateInputRef.current.showPicker) {
            dateInputRef.current.showPicker();
          } else {
            dateInputRef.current.click();
          }
        } catch (error) {
          dateInputRef.current.click();
        }
      }, 0);
    }
  };

  // Lightbox slides — built from the 4 image fields. Source is the local
  // data-URL preview captured during upload (no presigned URL exists yet on
  // the create screen since the report isn't saved).
  const gallerySlides = INITIAL_INSPECTION_IMAGE_FIELDS
    .map(({ field, label }) => {
      const src = photoPreviewUrls[field];
      if (!src) return null;
      return { field, src, title: label };
    })
    .filter(Boolean);

  const openGalleryAt = (field) => {
    const idx = gallerySlides.findIndex((s) => s.field === field);
    if (idx >= 0) {
      setGalleryIndex(idx);
      setGalleryOpen(true);
    }
  };

  const galleryRotations = gallerySlides.reduce((acc, slide, i) => {
    const r = photoRotations[slide.field];
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleGalleryRotate = (slideIdx) => {
    const slide = gallerySlides[slideIdx];
    if (slide) rotatePhotoField(slide.field);
  };

  // Renders a thumbnail tile under the file input once a file has been picked
  // & uploaded. Click → lightbox; rotation is applied as CSS until save.
  const renderImagePreview = (fieldName) => {
    const src = photoPreviewUrls[fieldName];
    if (!src) return null;
    const rotation = photoRotations[fieldName] || 0;
    return (
      <div style={{ marginTop: '8px' }}>
        <img
          src={src}
          alt={fieldName}
          onClick={() => openGalleryAt(fieldName)}
          title="Click to view larger"
          style={{
            maxWidth: '180px',
            maxHeight: '180px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '4px',
            cursor: 'zoom-in',
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.25s ease',
            display: 'block',
          }}
        />
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form submission started", { formData, errors });

    if (!validateForm()) {
      console.log("Form validation failed", errors);
      toast.error("Please fix the errors in the form");
      return;
    }

    const hasBufferedImages =
      Object.keys(draftRef.current.photos || {}).length > 0;
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;

    setLoading(true);

    // Offline, or images still buffered locally: queue for background sync.
    if (!isOnline || hasBufferedImages) {
      try {
        await queueForSync();
      } catch (err) {
        setLoading(false);
        toast.error(
          "Could not save this report for offline upload: " +
            (err?.message || "storage unavailable")
        );
      }
      return;
    }

    try {
      // Merge pending rotations. Backend rotates the S3 file in place and
      // strips these fields before DB insert.
      const submitData = { ...formData };
      Object.entries(photoRotations).forEach(([field, degrees]) => {
        if (degrees) submitData[`${field}_rotate`] = degrees;
      });

      const response = await initialInspectionReportsService.createInitialInspectionReport(
        propertyId,
        submitData
      );
      setPhotoRotations({});
      if (draftActiveRef.current) {
        await deleteDraft(draftRef.current.id).catch(() => {});
        draftActiveRef.current = false;
      }

      console.log("Form submission successful:", response);
      toast.success("Initial inspection report created successfully");
      navigate(`/properties/${propertyId}`);
    } catch (error) {
      console.error("Error creating initial inspection report:", error);

      // Network failure (no server response) → fall back to the offline queue.
      if (!error?.response) {
        try {
          await queueForSync();
          return;
        } catch (_) {
          /* fall through */
        }
      }

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        toast.error("Please fix the errors in the form");
      } else {
        toast.error(`Failed to create initial inspection report: ${error.message || "Unknown error"}`);
      }
      setLoading(false);
    }
  };

  const renderFormGroup = (
    label,
    name,
    type = "text",
    required = false,
    options = null,
    placeholder = ""
  ) => {
    const hasError = errors[name];
    const isDateField = name === 'report_date' || name === 'issued' || name === 'review_date';
    
    // Get the appropriate date input ref
    let dateInputRef = null;
    if (name === 'report_date') {
      dateInputRef = reportDateInputRef;
    } else if (name === 'issued') {
      dateInputRef = issuedDateInputRef;
    } else if (name === 'review_date') {
      dateInputRef = reviewDateInputRef;
    }

    return (
      <div className={styles.formGroup}>
        <label className={`${styles.controlLabel} ${styles.colSm6}`}>
          {label} {required && <span style={{ color: "#dc3545" }}>*</span>}
        </label>
        <div className={styles.colSm6}>
          {type === "select" ? (
            <select
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={`${styles.formControl} ${
                hasError ? styles.hasError : ""
              }`}
              required={required}
            >
              <option value="">Please select</option>
              {options &&
                Object.entries(options).map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
            </select>
          ) : type === "textarea" ? (
            <textarea
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={`${styles.formControl} ${
                hasError ? styles.hasError : ""
              }`}
              required={required}
              placeholder={placeholder}
              rows={6}
            />
          ) : isDateField ? (
            // Date field with calendar picker (Laravel style)
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name={name}
                value={formData[name] || ''}
                onChange={handleInputChange}
                className={`${styles.formControl} ${
                  hasError ? styles.hasError : ""
                }`}
                required={required}
                placeholder="dd/mm/yyyy"
                readOnly
                onClick={(e) => openDatePicker(name, e)}
                style={{ paddingRight: '35px', cursor: 'pointer' }}
              />
              {/* Hidden date input for native date picker */}
              <input
                ref={dateInputRef}
                type="date"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  zIndex: 10,
                  fontSize: '16px',
                  padding: '6px 35px 6px 10px',
                  margin: 0,
                  border: 'none',
                  background: 'transparent',
                  boxSizing: 'border-box'
                }}
                value={convertToDateInputFormat(formData[name])}
                onChange={(e) => handleDateChange(name, e)}
              />
              <span
                className="glyphicon glyphicon-calendar"
                onClick={(e) => openDatePicker(name, e)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  zIndex: 11,
                  fontSize: '14px'
                }}
              />
            </div>
          ) : (
            <input
              type={type}
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={`${styles.formControl} ${
                hasError ? styles.hasError : ""
              }`}
              required={required}
              placeholder={placeholder}
              {...(type === "number" && { min: "0" })}
            />
          )}
          {hasError && <div className={styles.errorMessage}>{hasError}</div>}
        </div>
      </div>
    );
  };

  const renderFileUpload = (label, name, required = false) => {
    const hasError = errors[name];

    return (
      <div className={styles.formGroup}>
        <label className={styles.controlLabel}>
          {label} {required && <span style={{ color: "#dc3545" }}>*</span>}
        </label>
        <div className={styles.colSm10}>
          <div className={styles.inputImage}>
            <input
              type="file"
              name={name}
              onChange={handleInputChange}
              className={`${styles.formControl} ${
                hasError ? styles.hasError : ""
              }`}
              accept="image/*"
              required={required}
            />
          </div>
          {hasError && <div className={styles.errorMessage}>{hasError}</div>}
        </div>
      </div>
    );
  };

  if (loading && !property) {
    return (
      <div className={styles.container}>
        <div className={styles.spinner}></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <a href="/properties" className={styles.breadcrumbItem}>
          Properties
        </a>
        <span className={styles.breadcrumbSeparator}>/</span>
        <a href={`/properties/${propertyId}`} className={styles.breadcrumbItem}>
          {property?.name || `Property ${propertyId}`}
        </a>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbItem}>Create Initial Inspection Report</span>
      </div>

      <div
        className={styles.panelForm}
        style={{ border: "none", backgroundColor: "#F8F9FA" }}
      >
        <div
          className={styles.panelHeading}
          style={{ backgroundColor: "#F8F9FA", border: "none" }}
        >
          <h3 className={styles.panelTitle} style={{ fontSize: "29px" }}>
            Create Initial Inspection Report
          </h3>
        </div>
        <div className={styles.panelBody}>
          <form onSubmit={handleSubmit} className={styles.formHorizontal}>
            <input type="hidden" name="property_id" value={propertyId} />

            {/* Address Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div
                  className={styles.reportSectionHeader}
                  style={{
                    fontSize: "29px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  Address
                </div>
                <div>
                  {renderFormGroup(
                    "Property owner",
                    "client",
                    "text",
                    false,
                    null,
                    ""
                  )}
                  {renderFormGroup(
                    "Address",
                    "address",
                    "text",
                    false,
                    null,
                    ""
                  )}
                </div>
              </div>
            </div>

            {/* General Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div
                  className={styles.reportSectionHeader}
                  style={{
                    fontSize: "29px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  General
                </div>
              </div>

              <div className={styles.colXs6}>
                {renderFormGroup("Status", "status_id", "select", true, {
                  0: "Unpublished",
                  1: "Published",
                })}
                {renderFormGroup(
                  "Building Type", // Fixed label to match Laravel staging
                  "building_type",
                  "select",
                  false,
                  buildingTypes.reduce(
                    (acc, type) => ({ ...acc, [type]: type }),
                    {}
                  )
                )}
                {renderFormGroup("How many floors", "floors", "number", false)}
                {renderFormGroup(
                  "How many bathrooms",
                  "bathrooms",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Is there parking",
                  "parking",
                  "select",
                  false,
                  { 0: "No", 1: "Yes" }
                )}
              </div>

              <div className={styles.colXs6}>
                {renderFormGroup("What Council", "what_council", "text", false)} {/* Fixed label to match Laravel staging */}
                {renderFormGroup(
                  "Property For Voa", // Fixed label to match Laravel staging
                  "property_for_voa",
                  "select",
                  false,
                  { 0: "No", 1: "Yes" }
                )}
                {renderFormGroup(
                  "How many toilets",
                  "toilets",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "How many kitchens",
                  "kitchens",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Is there a garden",
                  "garden",
                  "select",
                  false,
                  { 0: "No", 1: "Yes" }
                )}
              </div>
            </div>

            {/* Rooms Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div
                  className={styles.reportSectionHeader}
                  style={{
                    fontSize: "29px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  Rooms
                </div>

                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabelRequired} ${styles.colSm4}`}>
                    How many guardians / guardian rooms
                  </label>
                  <div className={styles.colSm2}>
                    <input
                      type="number"
                      name="rooms_number"
                      value={formData.rooms_number}
                      onChange={handleRoomsNumberChange}
                      className={styles.formControl}
                      required
                      min="0"
                    />
                    {errors.rooms_number && (
                      <div className={styles.errorMessage}>
                        {errors.rooms_number}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.roomsForms}>
              <div className={styles.row}>
                {Array.from({ length: roomsNumber }, (_, index) => (
                  <div key={index} className={styles.colXs3}>
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabelRequired} ${styles.colSm4}`}>
                        Room {index + 1}
                      </label>
                      <div className={styles.colSm8}>
                        <select
                          value={formData.rooms[index] || ""}
                          onChange={(e) =>
                            handleRoomChange(index, e.target.value)
                          }
                          className={styles.formControl}
                          required
                        >
                          <option value="">Please select</option>
                          {Object.entries(roomSizes).map(([value, text]) => (
                            <option key={value} value={value}>
                              {text}
                            </option>
                          ))}
                        </select>
                        {errors[`rooms.${index}`] && (
                          <div className={styles.errorMessage}>
                            {errors[`rooms.${index}`]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Maintenance Section */}
            <div
              className={styles.reportSectionHeader}
              style={{
                fontSize: "29px",
                fontWeight: "600",
                border: "none",
              }}
            >
              Maintenance
            </div>
            {renderFormGroup(
              "Maintenance", // Fixed label to match Laravel staging
              "maintenance",
              "textarea",
              false,
              null,
              ""
            )}

            {/* Installations Section */}
            <div
              className={styles.reportSectionHeader}
              style={{
                fontSize: "29px",
                fontWeight: "600",
                border: "none",
              }}
            >
              Installations <small>(How many to be installed)</small>
            </div>

            <div className={styles.row}>
              <div className={styles.colXs6}>
                {renderFormGroup(
                  "Shower Cubicles",
                  "installation_shower_cubicles",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Shower electric units",
                  "installation_shower_electric_units",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Over sink water heaters",
                  "installation_over_sink_water_heaters",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Mini ovens",
                  "installation_mini_ovens",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Microwaves",
                  "installation_microwaves",
                  "number",
                  false
                )}
              </div>

              <div className={styles.colXs6}>
                {renderFormGroup(
                  "Kitchens (worktops)",
                  "installation_kitchens",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Electric heaters",
                  "installation_electric_heaters",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Fridges",
                  "installation_fridges",
                  "number",
                  false
                )}
                {renderFormGroup(
                  "Cookers",
                  "installation_cookers",
                  "number",
                  false
                )}
              </div>
            </div>

            {/* General Utilities Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div
                  className={styles.reportSectionHeader}
                  style={{
                    fontSize: "29px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  General Utilities
                </div>

                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabel} ${styles.colSm3}`}>
                    List location of meters/boards/main switches
                  </label>
                  <div className={styles.colSm9}>
                    <textarea
                      name="locations_of_meters"
                      value={formData.locations_of_meters}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      rows={6}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.colXs3}>
                <div className={styles.mb10}>
                  <strong>Gas Meters</strong>
                </div>
                <div className={styles.fileUploadContainer}>
                  <input
                    type="file"
                    name="image_gas_meters"
                    onChange={handleInputChange}
                    className={styles.fileInput}
                    accept="image/*"
                    id="gas-meters-upload"
                  />
                  <label
                    htmlFor="gas-meters-upload"
                    className={styles.fileButton}
                  >
                    Choose File
                  </label>
                  <span className={styles.fileName}>
                    {fileNames.image_gas_meters}
                  </span>
                </div>
                {renderImagePreview('image_gas_meters')}
              </div>

              <div className={styles.colXs3}>
                <div className={styles.mb10}>
                  <strong>Electric Meters</strong>
                </div>
                <div className={styles.fileUploadContainer}>
                  <input
                    type="file"
                    name="image_electric_meters"
                    onChange={handleInputChange}
                    className={styles.fileInput}
                    accept="image/*"
                    id="electric-meters-upload"
                  />
                  <label
                    htmlFor="electric-meters-upload"
                    className={styles.fileButton}
                  >
                    Choose File
                  </label>
                  <span className={styles.fileName}>
                    {fileNames.image_electric_meters}
                  </span>
                </div>
                {renderImagePreview('image_electric_meters')}
              </div>

              <div className={styles.colXs3}>
                <div className={styles.mb10}>
                  <strong>Water Meters</strong>
                </div>
                <div className={styles.fileUploadContainer}>
                  <input
                    type="file"
                    name="image_water_meters"
                    onChange={handleInputChange}
                    className={styles.fileInput}
                    accept="image/*"
                    id="water-meters-upload"
                  />
                  <label
                    htmlFor="water-meters-upload"
                    className={styles.fileButton}
                  >
                    Choose File
                  </label>
                  <span className={styles.fileName}>
                    {fileNames.image_water_meters}
                  </span>
                </div>
                {renderImagePreview('image_water_meters')}
              </div>

              <div className={styles.colXs3}>
                <div className={styles.mb10}>
                  <strong>Fuse Boards</strong>
                </div>
                <div className={styles.fileUploadContainer}>
                  <input
                    type="file"
                    name="image_fuse_boards"
                    onChange={handleInputChange}
                    className={styles.fileInput}
                    accept="image/*"
                    id="fuse-boards-upload"
                  />
                  <label
                    htmlFor="fuse-boards-upload"
                    className={styles.fileButton}
                  >
                    Choose File
                  </label>
                  <span className={styles.fileName}>
                    {fileNames.image_fuse_boards}
                  </span>
                </div>
                {renderImagePreview('image_fuse_boards')}
              </div>

              <div className={`${styles.colXs12} ${styles.sectionSpacer}`}>
                <div className={styles.row}>
                  <div className={styles.colXs4}>
                    Utility Bills <small>Be specific to what bills</small>
                  </div>

                  <div className={styles.colXs4}>
                    {renderFormGroup(
                      "Client",
                      "utility_bills_client",
                      "text",
                      false
                    )}
                  </div>

                  <div className={styles.colXs4}>
                    {renderFormGroup("GGM", "utility_bills_ggm", "text", false)}
                  </div>
                </div>

                <div className={styles.row}>
                  <div className={styles.colXs4}>Waste Collection</div>

                  <div className={styles.colXs4}>
                    {renderFormGroup(
                      "Client",
                      "waste_collection_client",
                      "text",
                      false
                    )}
                  </div>

                  <div className={styles.colXs4}>
                    {renderFormGroup(
                      "GGM",
                      "waste_collection_ggm",
                      "text",
                      false
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Accountability Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div
                  className={styles.reportSectionHeader}
                  style={{
                    fontSize: "29px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  Accountability
                </div>
              </div>

              <div className={styles.colXs4}>
                {renderFormGroup("Surveyed By", "surveyed_by", "text", false)} {/* Fixed label to match Laravel staging */}
              </div>

              <div className={styles.colXs4}>
                {renderFormGroup("Signed", "signed", "text", false)} {/* Fixed label to match Laravel staging */}
              </div>

              <div className={styles.colXs4}>
                {renderFormGroup("Report Date", "report_date", "text", false)} {/* Fixed label to match Laravel staging */}
              </div>
            </div>

            {/* Footer Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div
                  className={styles.reportSectionHeader}
                  style={{
                    fontSize: "29px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  Footer
                </div>
              </div>

              <div className={styles.colXs3}>
                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabel} ${styles.colSm4} ${styles.iirSmallerText}`}>
                    Form
                  </label>
                  <div className={styles.colSm8}>
                    <input
                      type="text"
                      name="form"
                      value={formData.form}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      placeholder="Q15"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.colXs3}>
                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabel} ${styles.colSm4} ${styles.iirSmallerText}`}>
                    Issued
                  </label>
                  <div className={styles.colSm8} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="issued"
                      value={formData.issued || ''}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      placeholder="dd/mm/yyyy"
                      readOnly
                      onClick={(e) => openDatePicker('issued', e)}
                      style={{ paddingRight: '35px', cursor: 'pointer' }}
                    />
                    {/* Hidden date input for native date picker */}
                    <input
                      ref={issuedDateInputRef}
                      type="date"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 10,
                        fontSize: '16px',
                        padding: '6px 35px 6px 10px',
                        margin: 0,
                        border: 'none',
                        background: 'transparent',
                        boxSizing: 'border-box'
                      }}
                      value={convertToDateInputFormat(formData.issued)}
                      onChange={(e) => handleDateChange('issued', e)}
                    />
                    <span
                      className="glyphicon glyphicon-calendar"
                      onClick={(e) => openDatePicker('issued', e)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        zIndex: 11,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.colXs3}>
                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabel} ${styles.colSm4} ${styles.iirSmallerText}`}>
                    Review Date
                  </label>
                  <div className={styles.colSm8} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="review_date"
                      value={formData.review_date || ''}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      placeholder="dd/mm/yyyy"
                      readOnly
                      onClick={(e) => openDatePicker('review_date', e)}
                      style={{ paddingRight: '35px', cursor: 'pointer' }}
                    />
                    {/* Hidden date input for native date picker */}
                    <input
                      ref={reviewDateInputRef}
                      type="date"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 10,
                        fontSize: '16px',
                        padding: '6px 35px 6px 10px',
                        margin: 0,
                        border: 'none',
                        background: 'transparent',
                        boxSizing: 'border-box'
                      }}
                      value={convertToDateInputFormat(formData.review_date)}
                      onChange={(e) => handleDateChange('review_date', e)}
                    />
                    <span
                      className="glyphicon glyphicon-calendar"
                      onClick={(e) => openDatePicker('review_date', e)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        zIndex: 11,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.colXs3}>
                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabel} ${styles.colSm4} ${styles.iirSmallerText}`}>
                    Approved
                  </label>
                  <div className={styles.colSm8}>
                    <input
                      type="text"
                      name="approved"
                      value={formData.approved}
                      onChange={handleInputChange}
                      className={styles.formControl}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Notes Section */}
            <div
              className={styles.reportSectionHeader}
              style={{
                fontSize: "29px",
                fontWeight: "600",
                border: "none",
              }}
            >
              Additional Notes
            </div>
            {renderFormGroup("Notes", "notes", "textarea", false, null, "")} {/* Fixed label to match Laravel staging */}

            {/* Submit Button */}
            <div className={styles.formGroup}>
              <div className={styles.colSm12}>
                <div className={styles.textRight}>
                  <button
                    style={{ backgroundColor: "#1B2046" }}
                    type="submit"
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className={styles.spinner}></span>
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Large image viewer */}
      <PhotoGallery
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        slides={gallerySlides}
        index={galleryIndex}
        rotations={galleryRotations}
        onRotate={handleGalleryRotate}
      />
    </div>
  );
};

export default InitialInspectionCreate;
